// src/components/modals/DeletePurchaseOrderModal.jsx
import { useState } from 'react'
import { Button } from '../ui/Button'
import { X, AlertTriangle, Loader2, ShoppingCart } from 'lucide-react'
import PurchaseOrderService from '../../services/purchaseOrderService'
import { PurchaseOrderUtils } from '../../utils/purchaseOrderUtils'

const DeletePurchaseOrderModal = ({ isOpen, onClose, purchaseOrder, onOrderDeleted }) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  if (!isOpen || !purchaseOrder) return null

  const handleDelete = async () => {
    try {
      setLoading(true)
      setError('')
      
      console.log('🗑️ Deleting purchase order:', purchaseOrder._id)
      
      const response = await PurchaseOrderService.deletePurchaseOrder(purchaseOrder._id)
      
      if (response?.success) {
        console.log('✅ Purchase order deleted successfully')
        onOrderDeleted?.(purchaseOrder._id)
        onClose()
      } else {
        throw new Error(response?.message || 'Failed to delete purchase order')
      }
    } catch (error) {
      console.error('❌ Error deleting purchase order:', error)
      setError(error.message || 'Failed to delete purchase order')
    } finally {
      setLoading(false)
    }
  }

  const canDelete = purchaseOrder.status === 'pending'
  const statusConfig = PurchaseOrderUtils.getStatusConfig(purchaseOrder.status)

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="bg-red-100 p-2 rounded-lg">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Delete Purchase Order</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
            disabled={loading}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {canDelete ? (
            <>
              <div className="flex items-center justify-center mb-6">
                <div className="bg-red-100 p-4 rounded-full">
                  <ShoppingCart className="h-8 w-8 text-red-600" />
                </div>
              </div>

              <div className="text-center mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Are you sure you want to delete this Purchase Order?
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  This action cannot be undone.
                </p>

                {/* Purchase Order Details */}
                <div className="bg-gray-50 rounded-lg p-4 text-left">
                  <div className="grid grid-cols-1 gap-3 text-sm">
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700">PO Number:</span>
                      <span className="text-gray-900 font-mono">{purchaseOrder.poNumber || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700">Branch:</span>
                      <span className="text-gray-900">{purchaseOrder.requestingBranch?.name || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700">{purchaseOrder.supplierType === 'supplier' ? 'Supplier' : 'Warehouse'}:</span>
                      <span className="text-gray-900">
                        {purchaseOrder.supplier?.name || purchaseOrder.warehouse?.name || 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700">Order Date:</span>
                      <span className="text-gray-900">
                        {PurchaseOrderUtils.formatDate(purchaseOrder.orderDate)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700">Status:</span>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusConfig.bgColor} ${statusConfig.textColor}`}>
                        {statusConfig.label}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700">Total Items:</span>
                      <span className="text-gray-900">{purchaseOrder.orderItems?.length || 0}</span>
                    </div>
                  </div>
                </div>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleDelete}
                  disabled={loading}
                  className="bg-red-600 hover:bg-red-700 text-white flex items-center space-x-2"
                >
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                  <span>{loading ? 'Deleting...' : 'Delete'}</span>
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Cannot Delete Message */}
              <div className="text-center">
                <div className="bg-yellow-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <AlertTriangle className="h-8 w-8 text-yellow-600" />
                </div>
                
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Cannot Delete Purchase Order
                </h3>
                
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-yellow-800 mb-2">
                    <strong>This process can be done only when the status is Pending.</strong>
                  </p>
                  <p className="text-sm text-yellow-700">
                    The current status is <strong>{statusConfig.label}</strong>. 
                    Purchase orders can only be deleted when they are in "Pending" status.
                  </p>
                </div>

                {/* Purchase Order Details */}
                <div className="bg-gray-50 rounded-lg p-4 text-left mb-6">
                  <div className="grid grid-cols-1 gap-3 text-sm">
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700">PO Number:</span>
                      <span className="text-gray-900 font-mono">{purchaseOrder.poNumber || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700">Current Status:</span>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusConfig.bgColor} ${statusConfig.textColor}`}>
                        <span className="mr-1">{statusConfig.icon}</span>
                        {statusConfig.label}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700">Order Date:</span>
                      <span className="text-gray-900">
                        {PurchaseOrderUtils.formatDate(purchaseOrder.orderDate)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="text-center">
                  <Button
                    type="button"
                    onClick={onClose}
                    className="w-full"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default DeletePurchaseOrderModal